/**
 * Shared cart-line builder — the single source of truth for turning
 * a catalog PRODUCT + quotation-time CONFIG into the shape stored in
 * the cart state.
 *
 * Why this exists:
 *   `addItem` and `replaceItem` in store.js both build cart lines from
 *   a catalog product, but the two field-copy lists drifted three
 *   times in a row — most recently dropping `brand_description` on
 *   the Replace path, which silently wiped the Plywood/Blockboard/
 *   MDF/Doors brand-header row from every edited quotation's PDF.
 *
 *   Extracting this helper means the field list is defined ONCE.
 *   Any new PDF-relevant field added to the product schema only needs
 *   to be added here.
 *
 * NOT used by `addQuickVeneer` — those items don't have a catalog
 * product source (staff type them in), so brand-headers only appear
 * if the Quick Add dialog explicitly collects them.
 *
 * See:
 *   /app/frontend/src/lib/__tests__/replaceItem_brandDescription.test.js
 *   /app/frontend/src/lib/__tests__/loadQuotationForEdit_brandDescription.test.js
 */
import { sizeToSqft } from './sizeToSqftStandalone';


/**
 * Compose the human-readable cart-line description from a product.
 * Prefers subcategory / product_name, avoids duplicating the brand
 * when the product name already contains it (e.g. "Turakia Natural"
 * already contains "Turakia").
 */
export function buildCartItemDescription(product) {
  const productNameToUse = product.subcategory ||
    (product.product_name !== product.product_code ? product.product_name : '') ||
    product.product_name || '';

  const brandLower = (product.brand_name || '').toLowerCase().trim();
  const nameLower = (productNameToUse || '').toLowerCase().trim();

  if (!brandLower) return productNameToUse;
  if (nameLower.includes(brandLower)) return productNameToUse;
  if (nameLower === brandLower) return product.brand_name || '';
  return `${product.brand_name} ${productNameToUse}`.trim();
}


/**
 * Resolve the per-unit rate to store as `calculated_rate`.
 *
 * If the caller provided an explicit expanded `calculatedRate` we use
 * it as-is. Otherwise for per-sqft products we expand rate × sqft(size)
 * so an 8x4 sheet at ₹1,250/sqft becomes ₹40,000/sheet (not ₹1,250,
 * which would silently undercharge — the Milled Fluted iter-105 bug).
 */
export function resolveCalculatedRate({ product, size, calculatedRate, baseRate }) {
  const rawCalcRate = Number(
    calculatedRate ?? baseRate ?? product?.base_rate ?? 0
  ) || 0;

  const isPerSqft = (product?.rate_type === 'per sq ft');
  const callerSuppliedExpanded = (
    calculatedRate !== undefined && calculatedRate !== null
    && Number(calculatedRate) > 0
    && Number(calculatedRate) !== Number(baseRate ?? product?.base_rate ?? 0)
  );

  if (isPerSqft && !callerSuppliedExpanded && size) {
    const sqft = sizeToSqft(size, product?.main_category);
    if (sqft > 0) {
      return rawCalcRate * sqft;
    }
  }
  return rawCalcRate;
}


/**
 * Build a full cart item object from a catalog product + config.
 *
 * All PDF-relevant metadata (brand_description, subcategory, grade,
 * veneer_type, pack_size, variant_size, hardware_type, product_name,
 * description_text, show_brand, nail_number) is copied here so the
 * Detail page + PDF renderer always have what they need to rebuild
 * the brand-header row + per-item description.
 *
 * `overrides` merges in on top so callers (e.g. `replaceItem`) can
 * preserve fields from the original cart item — most importantly
 * `cart_id`, `section`, and the customer's discount settings.
 */
export function buildCartItemFromProduct(product, config, overrides = {}) {
  const {
    size, quantity, unit, calculatedRate,
    thickness, baseRate, woodenFlooring,
  } = config;

  const description = buildCartItemDescription(product);
  const safeCalcRate = resolveCalculatedRate({ product, size, calculatedRate, baseRate });
  const safeQty = Number(quantity) || 0;

  const base = {
    // A default cart_id derived from the product + size + timestamp so
    // freshly-added items get a unique key. Callers that want to keep
    // the ORIGINAL cart_id (e.g. Replace) pass it via `overrides`.
    cart_id: `${product.id}-${size}-${thickness}-${Date.now()}`,
    product_id: product.id,
    description,
    brand: product.brand_name,
    brand_description: product.brand_description || '',  // For PDF brand-header rows
    main_category: product.main_category || 'Others',
    size: size || product.size,
    thickness: thickness || product.thickness,
    quantity: quantity,
    unit: unit || 'NOS',
    rate: baseRate || product.base_rate || safeCalcRate,
    rate_type: product.rate_type,
    calculated_rate: safeCalcRate,
    discount_type: 'percent',
    discount_value: 0,
    cascading_discount: '',
    cascading_discount_type: 'percent',
    net_rate: safeCalcRate,
    amount: safeCalcRate * safeQty,
    // Product metadata for PDF formatting + brand-hiding logic
    color: product.color,
    feature: product.feature,
    hardware_system_type: product.hardware_system_type,
    product_code: product.product_code || '',
    species: product.species || product.veneer_type || product.veneer_species || '',
    finish: product.finish || '',
    nail_number: product.nail_number || '',
    // All PDF-relevant metadata — MUST include every field the PDF
    // renderer or Detail page reads (brand-header row + per-item
    // description depend on these). Add new PDF fields HERE and only
    // here — that's the whole point of this helper.
    subcategory: product.subcategory || '',
    grade: product.grade || '',
    veneer_type: product.veneer_type || '',
    pack_size: product.pack_size || '',
    variant_size: product.variant_size || '',
    hardware_type: product.hardware_type || '',
    product_name: product.product_name || '',
    description_text: product.description_text || '',
    show_brand: product.show_brand_in_pdf !== false,
    is_quick_add: false,
    // Wooden Flooring (box-billed). When present, the cart line + PDF
    // show "1 box = X sq ft | N boxes = M sq ft" and the amount is
    // computed from qty_boxes × box_rate (we sell sealed boxes only).
    wooden_flooring: woodenFlooring || null,
  };

  return { ...base, ...overrides };
}
